import { Component, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { AssignEmployeeService } from '../../services/assign-employee.service';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { ProfessionService } from '../../../core/profession.service';
import { PhaseWithEmployees } from '../domain/phase-with-employees';
import { NotificationService } from '../../../core/notification.service';
import { ProjectService } from '../../services/project.service';
import { EmployeeRecommendStep } from "../../shared/recommned-employee/employee-recommend-step";
import { RecommendEmployeeService } from "../../services/recommend-employee.service";
import {PhaseAndEmployees} from "../domain/phase-and-employees";

@Component({
  standalone:false,
  selector: 'app-recommend-employee',
  template: `
    <ng-container [ngSwitch]="step$.asObservable() | async">
      <ng-container *ngIf="project$ | async as project">
        <app-recommend-to-phases-step
          *ngSwitchCase="'phases'"
          [isPreviousStep]="false"
          [isNextStep]="true"
          [phases]="project?.phases"
          [phasesWithEmployees]="phasesWithEmployees$ | async"
          (closeEmployeeRecommendation)="onCloseEmployeeRecommendation()"
          (phaseSelected)="onPhaseSelected($event, project?.phases)"
        ></app-recommend-to-phases-step>
      </ng-container>
      <app-recommend-employees-step
        *ngSwitchCase="'employees'"
        [isPreviousStep]="true"
        [professions]="professions$ | async"
        [projectId]="(project$ | async)?.id"
        [phase]="phase$ | async"
        [recommendation]="recommendation$ | async"
        (closeEmployeeRecommendation)="onCloseEmployeeRecommendation()"
        (backToPreviousStep)="setStep('phases')"
        (employeeSelected)="onEmployeeSelected($event)"
      ></app-recommend-employees-step>
    </ng-container>
  `,
})
export class RecommendEmployeeFromProjectComponent implements OnInit {
  step$ = new BehaviorSubject<EmployeeRecommendStep>('phases');

  professions$ = this.professionService.professions$;
  phasesWithEmployees$ = new BehaviorSubject<PhaseWithEmployees[]>([]);

  project$ = this.assignEmployeeService.project$;
  phase$ = this.assignEmployeeService.phase$;
  employeeIds$ = this.assignEmployeeService.employeeIds$;
  recommendation$ = this.recommendEmployeeService.phase$;

  constructor(
    private readonly assignEmployeeService: AssignEmployeeService,
    private readonly recommendEmployeeService: RecommendEmployeeService,
    private readonly router: Router,
    private readonly professionService: ProfessionService,
    private readonly notificationService: NotificationService,
    private readonly route: ActivatedRoute,
    private readonly projectService: ProjectService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        take(1),
        map((paramMap) => paramMap.get('projectId')),
        switchMap((projectId) => {
          return this.projectService.getProject(projectId).pipe(
            tap((project) => this.assignEmployeeService.setProject(project)),
            switchMap(() => this.projectService.getProjectPhasesWithEmployees(projectId)),
          );
        })
      )
      .subscribe((phasesWithEmployees: PhaseWithEmployees[]) => {
        this.phasesWithEmployees$.next(phasesWithEmployees);
      });
  }

  setStep(value: EmployeeRecommendStep): void {
    this.step$.next(value);
  }

  onCloseEmployeeRecommendation(): void {
    this.route.url
      .pipe(take(1))
      .subscribe((url) => {
        if (url.some((segment) => segment.path === 'project-details'))
          this.navigateToProjectDetails();
        else if (url.some((segment) => segment.path === 'list'))
          this.navigateToProjectList();
      });
  }

  onPhaseSelected(phase: PhaseWithEmployees, otherPhases: PhaseAndEmployees[]): void {
    const matchingPhase = otherPhases.find(p => p.id === phase.id);

    if (matchingPhase && matchingPhase.requiredProfessions) {
      phase.requiredProfessions = matchingPhase.requiredProfessions;
    }

    this.assignEmployeeService.setPhase(phase);
    this.project$
      .pipe(
        take(1),
        switchMap(project =>
          this.projectService.getProjectPhasesWithRecommendedEmployees(project.id).pipe(
            map(phases => phases.find(ph => ph.id === phase.id))
          )
        )
      )
      .subscribe((recommendedPhase) => {
        this.recommendEmployeeService.setPhase(recommendedPhase);
      });
    this.setStep('employees');
  }

  onEmployeeSelected(employeeIds: string[]): void {
    this.assignEmployeeService.setEmployeeIds(employeeIds);

    combineLatest([this.project$, this.phase$, this.employeeIds$])
      .pipe(
        take(1),
        map(([project, phase, employeeIds]): [string, string, string[]] =>
          [project.id, phase.id, employeeIds]
        ),
      )
      .subscribe(([projectId, phaseId, employeeIds]) =>
          this.recommendEmployeeToPhase(projectId, phaseId, employeeIds)
      )
  }

  private recommendEmployeeToPhase(projectId: string, phaseId: string, employeeIds: string[]) {
    this.recommendEmployeeService.recommendEmployeeToPhase(<string[]>employeeIds, <string>projectId, <string>phaseId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.projectService.refreshProject(projectId);
          this.notificationService.setNotification('Employee was recommended to project.');
          this.onCloseEmployeeRecommendation();
        },
        error: (error) => {
          const errorMessage = error?.error?.msg || 'An error occurred while assigning the employee.';
          this.notificationService.setNotification(errorMessage, undefined, { severity: 'error' });
        }
      });
  }

  private navigateToProjectDetails(): void {
    this.project$
      .pipe(
        take(1),
        map((project => project.id)),
      )
      .subscribe((projectId) =>
        this.router.navigate(['/main', 'projects', 'project-details', projectId]),
      );
  }

  private navigateToProjectList(): void {
    this.router.navigate(['/main', 'projects', 'list']);
  }
}
